import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { createListing } from '../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function SellDialog({ holding, open, onOpenChange, onListed }) {
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (holding) setPrice(String(holding.currentValue || ''));
  }, [holding]);

  if (!holding) return null;
  const valuation = Number(holding.currentValue || 0);

  const submit = async (e) => {
    e.preventDefault();
    const ask = Number(price);
    if (!ask || ask <= 0) return toast.error('Enter a valid asking price');
    if (ask > valuation) return toast.error('Asking price must be at or below current valuation');
    setSubmitting(true);
    try {
      await createListing({ holdingId: holding.holdingId, askPrice: ask });
      toast.success('Listed on the marketplace');
      onOpenChange(false);
      onListed && onListed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not list holding');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-farmway-green-dark text-farmway-cream">
        <DialogHeader>
          <span className="eyebrow">Sell holding</span>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">{holding.name}</DialogTitle>
          <DialogDescription className="text-farmway-cream/70">
            List your position for other investors to bid on. Funds settle off-platform; ownership transfers on Farmway after an admin confirms settlement.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-xl bg-white/5 p-4">
            <p className="text-xs uppercase tracking-wider text-farmway-cream/50">Current valuation</p>
            <p className="font-display text-2xl font-black text-farmway-cream">{money(valuation)}</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-farmway-cream/80">Asking price (USD)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)}
              className="border-white/15 bg-white/5 text-farmway-cream focus-visible:ring-farmway-gold" />
            <p className="text-xs text-farmway-cream/40">Must be at or below {money(valuation)}.</p>
          </div>
          <button type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-70">
            <Tag className="h-4 w-4" /> {submitting ? 'Listing…' : 'List for sale'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
